"use client"

import { useRef, type ComponentProps } from "react"
import clsx from "clsx"
import { FormProvider as ReactHookFormProvider, useForm, type FieldValues } from "react-hook-form"

import {
  formDictionary,
  sanitizeFacetValueString,
  TemplatesProvider,
  unsanitizeFacetValueString,
} from "@unlikelystudio/react-ecommerce-hooks"

import { Facet } from "~/components/ui/Panels/Filters/components/Facet"
import { FilterPills } from "~/components/ui/Panels/Filters/components/FilterPills"
import { FiltersHeader } from "~/components/ui/Panels/Filters/components/FiltersHeader"
import { FiltersResetButton } from "~/components/ui/Panels/Filters/components/FiltersResetButton"
import { FiltersSubmitButton } from "~/components/ui/Panels/Filters/components/FiltersSubmitButton"
import type { SquareCtaProps } from "~/components/ui/SquareCta"
import { Drawer, DrawerContent } from "~/components/shared/Drawer/index.client"
import { useAlgoliaCustomContext } from "~/providers/AlgoliaCustomContext/index.client"
import { isObject } from "~/utils/is-object"
import { isString } from "~/utils/is-string"
import { objectEntries } from "~/utils/object-entries"
import { objectKeys } from "~/utils/object-keys"

import * as css from "./styles.css"

function FiltersPanel({ open, onOpenChange }: Required<Pick<ComponentProps<typeof Drawer>, "open" | "onOpenChange">>) {
  const panelRef = useRef<HTMLDivElement>(null)

  const { facets, setPage, selectedFilters, setSelectedFilters } = useAlgoliaCustomContext()

  const form = useForm({
    reValidateMode: "onSubmit",
    defaultValues: objectEntries(selectedFilters ?? {})?.reduce((acc, [key, values]) => {
      return {
        ...acc,
        [key]: values?.reduce((valuesAsObject, value) => {
          const sanitizedValue = sanitizeFacetValueString(value)
          return {
            ...valuesAsObject,
            [sanitizedValue]: sanitizedValue,
          }
        }, {}),
      }
    }, {}),
  })

  function onSubmit(data: FieldValues) {
    const facetsExcludingSort = objectKeys(data)?.filter((key) => key !== "sort")

    const newFiltersSelection = facetsExcludingSort?.reduce<typeof selectedFilters>((acc, facetName) => {
      const values = data?.[facetName]

      if (isObject(values)) {
        const selectedValues =
          objectEntries(values)
            ?.filter(([_, value]) => Boolean(value))
            ?.map(([key]) => {
              return key
            }) ?? []

        return {
          ...acc,
          [facetName]: selectedValues?.filter(isString)?.map(unsanitizeFacetValueString),
        }
      }

      return { ...acc, [facetName]: [] }
    }, {})

    setPage(1)

    setSelectedFilters(newFiltersSelection)
    onOpenChange?.(false)
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <div className={clsx(css.wrapper)}>
          <div ref={panelRef} className={css.FiltersPanel}>
            <TemplatesProvider
              dictionary={formDictionary}
              components={{
                button: (props) => {
                  return (
                    <FiltersResetButton
                      {...(props as SquareCtaProps)}
                      className={css.button}
                      onClick={(e) => {
                        ;(props as SquareCtaProps)?.onClick?.(e)
                      }}
                    />
                  )
                },
                facet: (props) => {
                  return <Facet {...props} />
                },
                input: (props) => {
                  if (props?.type === "submit") {
                    return (
                      <FiltersSubmitButton
                        {...(props as SquareCtaProps)}
                        onClick={(e) => {
                          ;(props as SquareCtaProps)?.onClick?.(e)
                        }}
                        className={css.button}
                      />
                    )
                  }

                  return null as unknown as JSX.Element
                },
              }}
            >
              <ReactHookFormProvider {...form}>
                <form className={css.content} onSubmit={form.handleSubmit((data) => onSubmit?.(data))}>
                  <FiltersHeader className={css.header} />
                  <FilterPills />
                  <div className={css.facets}>
                    {objectKeys(facets ?? {})?.map((name, index) => (
                      <Facet key={`facet-${name}-${index}`} {...{ name, index }} />
                    ))}
                  </div>
                  <div className={css.buttons}>
                    <FiltersResetButton
                      className={css.button}
                      onClick={() => {
                        onOpenChange?.(false)
                      }}
                    />
                    <FiltersSubmitButton className={css.button} onClick={() => {}} />
                  </div>
                </form>
              </ReactHookFormProvider>
            </TemplatesProvider>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

export { FiltersPanel }
